import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { withoutForbiddenElevation } from '@application/data-access';
import { fromWireEnums, type Guid } from '../entities/academy-enums.model';
import { LiveQuestionResponse } from '../entities/academy.model';
import { UpvoteResponse } from '../entities/academy-responses.model';

/**
 * HTTP client for the live Q&A endpoints (`/LiveQuestions/*` and the viewer list under
 * `/LiveEvents/{id}/questions`). Source of truth: `docs/academy-api-integration.md` §7.3.
 * PascalCase. Live SENDING/broadcast is the SignalR hub's job — this client only does reads
 * and moderation.
 */
@Injectable({ providedIn: 'root' })
export class LiveQuestionsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;

  /**
   * `GET /LiveEvents/{id}/questions` — the live's questions as a viewer reads them (backend
   * 014): most voted first, each with the asker's `PersonName`, never the asker's account. On
   * a single-room live it holds every account's questions; on a live from before 014 the
   * server returns only the caller's account, so the one read serves both (the same reach as
   * the hub pushes). The plain REST array serializes `LiveQuestionStatusType` as its numeric
   * code, translated to the name here.
   * ⚠ 403 outside the event's audience · 404 when the live is not visible to the caller:
   * marked `withoutForbiddenElevation()` so the watch page does not open the
   * temporary-elevation modal over a read it did alone.
   */
  public list(liveEventId: Guid): Observable<LiveQuestionResponse[]> {
    return this.http
      .get<LiveQuestionResponse[]>(
        `${this.baseUrl}/LiveEvents/${encodeURIComponent(liveEventId)}/questions`,
        { context: withoutForbiddenElevation(), withCredentials: true },
      )
      .pipe(map((list) => fromWireEnums(list)));
  }

  /**
   * `GET /LiveQuestions/producer?liveEventId={id}` — event owner/producer reads EVERY
   * question of the event, crossing the per-tenant Q&A isolation. The audience's own
   * reads stay tenant-scoped; only the event owner (owning-tenant staff, or Actuar on
   * Platform lives) is authorized here (403 otherwise). Use this on the producer console.
   * Items carry `PersonName` (backend 013d). The plain REST array serializes
   * `LiveQuestionStatusType` as its numeric code, translated to the name here.
   */
  public listForProducer(
    liveEventId: Guid
  ): Observable<LiveQuestionResponse[]> {
    return this.http
      .get<LiveQuestionResponse[]>(`${this.baseUrl}/LiveQuestions/producer`, {
        params: new HttpParams().set('liveEventId', liveEventId),
        withCredentials: true,
      })
      .pipe(map((list) => fromWireEnums(list)));
  }

  /**
   * `POST /LiveQuestions` — ask a question for a live event.
   * 🔑 identity from token: body is EXACTLY `{ LiveEventId, Content }`, never a `PersonId`.
   * The REST echo can carry `LiveQuestionStatusType` as its numeric code, as `listForProducer`
   * does; it is translated to the name here (a name passes through untouched).
   */
  public ask(input: {
    LiveEventId: Guid;
    Content: string;
  }): Observable<LiveQuestionResponse> {
    return this.http
      .post<LiveQuestionResponse>(
        `${this.baseUrl}/LiveQuestions`,
        { LiveEventId: input.LiveEventId, Content: input.Content },
        { withCredentials: true }
      )
      .pipe(map((question) => fromWireEnums(question)));
  }

  /**
   * `POST /LiveQuestions/{id}/upvote` — upvote a question (idempotent).
   * 🔑 identity from token: empty body, never a `PersonId`.
   */
  public upvote(id: Guid): Observable<UpvoteResponse> {
    return this.http.post<UpvoteResponse>(
      `${this.baseUrl}/LiveQuestions/${encodeURIComponent(id)}/upvote`,
      {},
      { withCredentials: true }
    );
  }

  /**
   * `PATCH /LiveQuestions/{id}/status` — moderate a question (Answered / Dismissed).
   * `status` is a required query param; sends an empty body.
   */
  public setStatus(
    id: Guid,
    status: 'Answered' | 'Dismissed'
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/LiveQuestions/${encodeURIComponent(id)}/status`,
      {},
      { params: new HttpParams().set('status', status), withCredentials: true }
    );
  }
}
